// ── Lab engine constants ────────────────────────────────────────────────────────────────────────────
// Sizes and palettes. Product values are copied EXACTLY from their source components (noted inline) so
// the lab widgets stay pixel-twins of the real DeckGen ones.

// Benchmark item grid.
export const ITEM_W = 320
export const ITEM_H = 180
export const GAP = 48

// Realistic-mix stress grid: each item sits centred in a square cell at its REAL DeckGen size — identical
// to what the tool rail places (SLIDE_SIZE / NOTE_SIDE / DOC_SIZE below) — so a grid slide and a freshly
// placed slide are pixel-for-pixel the same, and slides/notes/docs/images read as their true proportions.
export const BENCH_CELL = 2200 // square cell pitch — fits the largest item (a 1920×1080 slide) + a gap
export const BENCH_SIZES: Record<'slide' | 'note' | 'doc' | 'image', { w: number; h: number }> = {
  slide: { w: 1920, h: 1080 }, // = SLIDE_SIZE
  note: { w: 428, h: 428 }, // = NOTE_SIDE (square)
  doc: { w: 764, h: 1080 }, // = DOC_SIZE (portrait)
  image: { w: 800, h: 600 }, // = the tool-rail image size
}
// Kind mix cycled across the grid (~50% slides, 20% notes, 20% docs, 10% images).
export const BENCH_KIND_PATTERN: Array<'slide' | 'note' | 'doc' | 'image'> = [
  'slide', 'slide', 'note', 'slide', 'doc', 'image', 'slide', 'note', 'doc', 'slide',
]

// Camera limits.
export const MIN_ZOOM = 0.02
export const MAX_ZOOM = 256 // 25600%, so you can push it like paper.design

// engine renderer: DOM cards mount (real content) only when they're at least this wide on screen, and
// never more than this many at once. Below the threshold / above the cap the whiteboard sketch shows.
// A generous cap so REAL content — not the sketch — fills the medium zoom levels people actually read
// and edit at (~25-66%). Below the cap the sketch still carries the far field at overview zoom.
export const MOUNT_MIN_PX = 48
export const MOUNT_CAP = 40 // grid items
export const NOTE_MOUNT_CAP = 12
export const SLIDE_MOUNT_CAP = 8 // placed slides with content
// Mount/cull margin, in SCREEN px: an item this far outside the viewport still counts as "on screen" so it
// is already mounted (crisp) before it scrolls into view, never popping in at the edge.
export const MOUNT_MARGIN_PX = 100

// Product sizes: a slide is a 1920x1080 canvas (types/presentation.ts CANVAS_WIDTH); a document card is
// 764x1080 (canvasItemActions DOCUMENT_CARD_DEFAULT_SIZE). Both render flat (no shadow) with a 24px
// radius and a hairline edge so white-on-white still reads.
export const SLIDE_SIZE = { w: 1920, h: 1080 }
export const DOC_SIZE = { w: 764, h: 1080 }
export const CARD_RADIUS = 24

// The real DeckGen note, replicated: 428x428 square sticky (canvasItemActions NOTE_SIDE), radius 32,
// padding 40, Inter (UserNoteCards.tsx + Note.module.css).
export const NOTE_SIDE = 428
export const NOTE_RADIUS = 32
export const NOTE_PAD = 40
// Product order (UserNoteCards NOTE_COLORS): default WHITE first — new notes are white, color is
// changed from the note's floating toolbar, exactly like DeckGen.
export const NOTE_FILLS = [
  '#ffffff', // default
  'hsl(54 96% 89%)', // yellow
  'hsl(37 86% 92%)', // orange
  'hsl(0 70% 94%)', // red
  'hsl(134 76% 94%)', // green
  'hsl(217 89% 93%)', // blue
  'hsl(240 5% 96%)', // gray
]
// The same palette in the product's NOTE_COLORS shape (key/label/fill) — fed to the REAL
// CanvasItemToolbar's colorMenu. Indices align with NOTE_FILLS (shape.color stores the index).
export const NOTE_COLOR_OPTIONS = [
  { key: 'default', label: 'Default', fill: '#ffffff' },
  { key: 'yellow', label: 'Yellow', fill: 'hsl(54 96% 89%)' },
  { key: 'orange', label: 'Orange', fill: 'hsl(37 86% 92%)' },
  { key: 'red', label: 'Red', fill: 'hsl(0 70% 94%)' },
  { key: 'green', label: 'Green', fill: 'hsl(134 76% 94%)' },
  { key: 'blue', label: 'Blue', fill: 'hsl(217 89% 93%)' },
  { key: 'gray', label: 'Gray', fill: 'hsl(240 5% 96%)' },
]

export const SAMPLE_TEXTS = ['Deck', 'Hello world', 'Storytelling', 'Ship it', 'Fast + sharp', 'Canvas engine', 'Paper vibes', 'Guri was here', 'Type here', 'The lab']

// Debug-panel item counts.
export const COUNTS = [10, 20, 40, 50, 100, 500, 1000, 2000, 3000, 5000, 10000, 20000, 50000]
export const countLabel = (n: number) => (n >= 1000 ? `${n / 1000}k` : String(n))

// Small-deck fast path: at or below this many items, mount them ALL as real crisp DOM (lift the
// on-screen-size gate + the mount cap) so a small canvas is always sharp and never shows the bitmap layer.
// Cheap at these counts — a few dozen twins re-raster in ~nothing — and it stops at higher counts where the
// whiteboard virtualization is actually needed. The whiteboard still draws underneath as a safety net.
export const SMALL_DECK_MAX = 50
